import { Controller } from "@hotwired/stimulus"

// Controller for managing the purchase summary sidebar
// Listens for donation add/remove/update events and refreshes the summary
export default class extends Controller {
  static targets = [
    "donationsSection",
    "donationsSubtotal",
    "feeCoverage",
    "grandTotal"
  ]

  static values = {
    cartId: String
  }

  connect() {
    console.log("Purchase Summary Controller connected")

    // Bind event handlers
    this.boundHandleDonationUpdated = this.handleDonationUpdated.bind(this)
    this.boundHandleDonationRemoved = this.handleDonationRemoved.bind(this)
    this.boundHandleDonationAdded = this.handleDonationAdded.bind(this)

    // Listen for donation events from org-donation and organization-selector controllers
    // Stimulus dispatch creates events like "org-donation:donationUpdated" that bubble up
    document.addEventListener('org-donation:donationUpdated', this.boundHandleDonationUpdated)
    document.addEventListener('org-donation:donationRemoved', this.boundHandleDonationRemoved)
    document.addEventListener('organization-selector:donationAdded', this.boundHandleDonationAdded)
  }

  disconnect() {
    document.removeEventListener('org-donation:donationUpdated', this.boundHandleDonationUpdated)
    document.removeEventListener('org-donation:donationRemoved', this.boundHandleDonationRemoved)
    document.removeEventListener('organization-selector:donationAdded', this.boundHandleDonationAdded)
  }

  handleDonationAdded(event) {
    console.log("Purchase Summary: Donation added", event.detail)
    const { donation, cartTotal, organizationName } = event.detail

    // Add a new donation row to the summary
    this.addDonationRow(donation, organizationName)

    // Update totals
    this.refreshTotals()
  }

  handleDonationRemoved(event) {
    console.log("Purchase Summary: Donation removed", event.detail)
    const { itemId, orgId, cartTotal } = event.detail

    // Remove the donation row from the summary
    this.removeDonationRow(itemId, orgId)

    // Update totals after a short delay to ensure the org-donation element
    // has been removed from the DOM (the event is dispatched before removal)
    console.log("Purchase Summary: Scheduling refreshTotals after donation removal")
    setTimeout(() => {
      console.log("Purchase Summary: Calling refreshTotals after donation removal")
      this.refreshTotals()
    }, 100)
  }

  handleDonationUpdated(event) {
    console.log("Purchase Summary: Donation updated", event.detail)
    const { itemId, donation, cartTotal } = event.detail

    // Update the donation amount in the summary
    this.updateDonationRow(donation)

    // Update totals
    this.refreshTotals()
  }

  addDonationRow(donation, organizationName) {
    if (!this.hasDonationsSectionTarget) return

    const amount = parseFloat(donation.amount) || 0
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

    const rowHTML = `
      <div class="donation-row d-flex justify-content-between align-items-start mb-2"
           data-donation-item-id="${donation.id}"
           data-donation-org-id="${donation.organization_id}">
        <div class="donation-info">
          <div class="donation-org fw-bold">${this.escapeHtml(organizationName)}</div>
          <div class="donation-label text-muted small"><span class="donation-label-text">Donation Amount</span></div>
        </div>
        <div class="donation-amount" data-donation-amount-for-org="${donation.organization_id}">
          ${formatter.format(amount)}
        </div>
      </div>
    `

    this.donationsSectionTarget.insertAdjacentHTML('beforeend', rowHTML)
  }

  removeDonationRow(itemId, orgId) {
    // Try to find by item ID first, then by org ID
    let row = this.element.querySelector(`[data-donation-item-id="${itemId}"]`)
    if (!row && orgId) {
      row = this.element.querySelector(`[data-donation-org-id="${orgId}"]`)
    }

    if (row) {
      row.remove()
    }
  }

  updateDonationRow(donation) {
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    const amount = parseFloat(donation.amount) || 0

    // Find the amount element for this organization
    const amountEl = this.element.querySelector(`[data-donation-amount-for-org="${donation.organization_id}"]`)
    if (amountEl) {
      amountEl.textContent = formatter.format(amount)
    }
  }

  refreshTotals() {
    // Calculate totals from all org donation controllers on the page
    let totalDonations = 0
    let totalFeeCoverage = 0

    const orgDonationElements = document.querySelectorAll('[data-controller~="org-donation"]')
    console.log("Purchase Summary refreshTotals: Found", orgDonationElements.length, "org-donation elements")

    orgDonationElements.forEach(el => {
      const controller = this.application.getControllerForElementAndIdentifier(el, 'org-donation')
      if (controller) {
        const amount = controller.donationAmount || 0
        totalDonations += amount

        // Check if fee coverage is enabled for this org
        if (controller.hasFeeCoverageTarget && controller.feeCoverageTarget.checked) {
          const orgFee = (amount * 0.05) + 0.30
          const orgFeeCoverage = orgFee / (1 - 0.05)
          totalFeeCoverage += orgFeeCoverage
        }
      }
    })

    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

    if (this.hasDonationsSubtotalTarget) {
      this.donationsSubtotalTarget.textContent = formatter.format(totalDonations)
    }

    if (this.hasFeeCoverageTarget) {
      this.feeCoverageTarget.textContent = formatter.format(totalFeeCoverage)
    }

    if (this.hasGrandTotalTarget) {
      const grandTotal = totalDonations + totalFeeCoverage
      this.grandTotalTarget.textContent = formatter.format(grandTotal)
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }
}
